import React, { useEffect, useState } from 'react';
import { Table, Button, Container, Alert, Pagination, Modal, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { universityAPI } from '../../api/wbuoaApi';
import { FaEdit, FaTrash, FaExternalLinkAlt, FaPlus, FaFileImport, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const UniversityList = () => {
  const [universities, setUniversities] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentUniversity, setCurrentUniversity] = useState(null);
  const [formData, setFormData] = useState({
    University: '',
    Address: '',
    User_Code: '',
    url: '',
    mobile: '',
    member: 0,
    association_name: ''
  });
  const [searchFilters, setSearchFilters] = useState({
    University: '',
    Address: '',
    User_Code: '',
    url: '',
    mobile: '',
    member: '',
    association_name: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingU_ID, setDeletingU_ID] = useState(null);

  const navigate = useNavigate();

  // Style for dark green labels
  const labelStyle = {
    color: '#006400',
    fontWeight: 'bold'
  };

  // Fetch universities when page changes or filters change
  useEffect(() => {
    fetchUniversities();
  }, [currentPage, searchFilters]);

  // Handle import button click
  const handleImport = () => {
    navigate('/universities/import');
  };

  // Handle university deletion
  const handleDelete = async (U_ID) => {
    if (!U_ID) {
      setError('Invalid university identifier');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this university?')) {
      return;
    }

    setDeletingU_ID(U_ID);
    try {
      await universityAPI.deleteUniversity(U_ID);
      setUniversities(prev => prev.filter(uni => uni.U_ID !== U_ID));
      setSuccess('University deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to delete university: ${err.message || 'Server error'}`);
    } finally {
      setDeletingU_ID(null);
    }
  };

  // Handle edit button click
  const handleEdit = (university) => {
    if (!university?.U_ID) {
      setError('Invalid university data');
      return;
    }

    setCurrentUniversity(university);
    setFormData({
      University: university.University || '',
      Address: university.Address || '',
      User_Code: university.User_Code || '',
      url: university.url || '',
      mobile: university.mobile || '',
      member: university.member || 0,
      association_name: university.association_name || ''
    });
    setShowEditModal(true);
  };

  // Handle create new university
  const handleCreate = () => {
    setCurrentUniversity(null);
    setFormData({
      University: '',
      Address: '',
      User_Code: '',
      url: '',
      mobile: '',
      member: 0,
      association_name: ''
    });
    setShowCreateModal(true);
  };

  // Handle university update
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentUniversity?.U_ID) {
      setError('No university selected for update');
      return;
    }

    setIsUpdating(true);
    try {
      await universityAPI.updateUniversity(currentUniversity.U_ID, formData);
      setShowEditModal(false);
      fetchUniversities();
      setSuccess('University updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to update university: ${err.message || 'Please try again'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle create new university submission
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await universityAPI.createUniversity(formData);
      setShowCreateModal(false);
      fetchUniversities();
      setSuccess('University created successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to create university: ${err.message || 'Please try again'}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    });
  };

  // Handle search filter changes
  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Clear all search filters
  const handleClearFilters = () => {
    setSearchFilters({
      University: '',
      Address: '',
      User_Code: '',
      url: '',
      mobile: '',
      member: '',
      association_name: ''
    });
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Fetch universities with filters
  const fetchUniversities = async () => {
    setIsLoading(true);
    try {
      // Remove empty filters
      const activeFilters = Object.fromEntries(
        Object.entries(searchFilters).filter(([_, value]) => value !== '')
      );

      const response = await universityAPI.listUniversities(currentPage, activeFilters);
      console.log('API Response:', response);

      setUniversities(response.data);
      setTotalPages(response.pagination.total_pages || 1);
      setError(null);
    } catch (err) {
      setError('Failed to fetch universities: ' + (err.message || 'Server error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Render pagination with guaranteed visibility
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="mt-4 d-flex justify-content-center" style={{ padding: '20px 0' }}>
        <Pagination>
          <Pagination.Prev
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          />
          {Array.from({ length: totalPages }, (_, i) => (
            <Pagination.Item
              key={i + 1}
              active={i + 1 === currentPage}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    );
  };

  // Format member status for display
  const formatMemberStatus = (member) => {
    return member === 1 ? 'Yes' : 'No';
  };

  return (
    <Container className="py-4">
      {/* Header Section with Import and Add buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={handleImport}
            className="d-flex align-items-center"
          >
            <FaFileImport className="me-2" />
            Import Universities
          </Button>
        </div>
        <h2 className="mb-0 text-center flex-grow-1">Universities List</h2>
        <Button variant="primary" onClick={handleCreate}>
          <FaPlus className="me-2" />
          Add University
        </Button>
      </div>

      {/* Search Filters Section */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">
            <FaSearch className="me-2" />
            Search Filters
          </h5>
        </div>
        <div className="card-body">
          <Row>
            <Col md={6} lg={3}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>University Name</Form.Label>
                <Form.Control
                  type="text"
                  name="University"
                  value={searchFilters.University}
                  onChange={handleSearchChange}
                  placeholder="Search by name..."
                />
              </Form.Group>
            </Col>
            <Col md={6} lg={3}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="Address"
                  value={searchFilters.Address}
                  onChange={handleSearchChange}
                  placeholder="Search by address..."
                />
              </Form.Group>
            </Col>
            <Col md={6} lg={2}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>User Code</Form.Label>
                <Form.Control
                  type="text"
                  name="User_Code"
                  value={searchFilters.User_Code}
                  onChange={handleSearchChange}
                  placeholder="Search code..."
                />
              </Form.Group>
            </Col>
            <Col md={6} lg={2}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Member</Form.Label>
                <Form.Select
                  name="member"
                  value={searchFilters.member}
                  onChange={handleSearchChange}
                >
                  <option value="">All</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} lg={2}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Association</Form.Label>
                <Form.Control
                  type="text"
                  name="association_name"
                  value={searchFilters.association_name}
                  onChange={handleSearchChange}
                  placeholder="Search association..."
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex justify-content-end">
            <Button variant="outline-secondary" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Alerts for success/error messages */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mt-3">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mt-3">
          {success}
        </Alert>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Universities Table */}
          <div className="table-responsive mt-3">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>University</th>
                  <th>Address</th>
                  <th>User Code</th>
                  <th>Website</th>
                  <th>Contact</th>
                  <th>Member</th>
                  <th>Association</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {universities.length > 0 ? (
                  universities.map(uni => (
                    <tr key={uni.U_ID}>
                      <td>{uni.University}</td>
                      <td>{uni.Address}</td>
                      <td>{uni.User_Code}</td>
                      <td>
                        {uni.url && (
                          <Button
                            variant="link"
                            href={uni.url.startsWith('http') ? uni.url : `https://${uni.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Visit Website"
                            className="p-0"
                          >
                            <FaExternalLinkAlt />
                          </Button>
                        )}
                      </td>
                      <td>{uni.mobile}</td>
                      <td>
                        <span className={`badge ${uni.member === 1 ? 'bg-success' : 'bg-secondary'}`}>
                          {formatMemberStatus(uni.member)}
                        </span>
                      </td>
                      <td>{uni.association_name}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          onClick={() => handleEdit(uni)}
                          className="me-2"
                          title="Edit"
                          disabled={deletingU_ID !== null}
                          size="sm"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={() => handleDelete(uni.U_ID)}
                          title="Delete"
                          disabled={deletingU_ID !== null}
                          size="sm"
                        >
                          {deletingU_ID === uni.U_ID ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <FaTrash />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      No universities found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* PAGINATION */}
          {renderPagination()}
        </>
      )}

      {/* Edit University Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>Edit University</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdate}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>University Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="University"
                    value={formData.University}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>User Code *</Form.Label>
                  <Form.Control
                    type="text"
                    name="User_Code"
                    value={formData.User_Code}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>Address *</Form.Label>
              <Form.Control
                type="text"
                name="Address"
                value={formData.Address}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Website URL *</Form.Label>
                  <Form.Control
                    type="url"
                    name="url"
                    value={formData.url}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Contact Number *</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Association Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="association_name"
                    value={formData.association_name}
                    onChange={handleChange}
                    placeholder="Enter association name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={labelStyle}>Member Status</Form.Label>
                  <div>
                    <Form.Check
                      type="switch"
                      id="member-switch-edit"
                      name="member"
                      checked={formData.member === 1}
                      onChange={(e) => setFormData({
                        ...formData,
                        member: e.target.checked ? 1 : 0
                      })}
                      label={formData.member === 1 ? 'Member (Yes)' : 'Member (No)'}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end border-top pt-3">
              <Button variant="outline-secondary" onClick={() => setShowEditModal(false)} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Updating...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Create University Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>Add New University</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>University Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="University"
                    value={formData.University}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>User Code *</Form.Label>
                  <Form.Control
                    type="text"
                    name="User_Code"
                    value={formData.User_Code}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>Address *</Form.Label>
              <Form.Control
                type="text"
                name="Address"
                value={formData.Address}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Website URL *</Form.Label>
                  <Form.Control
                    type="url"
                    name="url"
                    value={formData.url}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Contact Number *</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Association Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="association_name"
                    value={formData.association_name}
                    onChange={handleChange}
                    placeholder="Enter association name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={labelStyle}>Member Status</Form.Label>
                  <div>
                    <Form.Check
                      type="switch"
                      id="member-switch-create"
                      name="member"
                      checked={formData.member === 1}
                      onChange={(e) => setFormData({
                        ...formData,
                        member: e.target.checked ? 1 : 0
                      })}
                      label={formData.member === 1 ? 'Member (Yes)' : 'Member (No)'}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end border-top pt-3">
              <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isCreating}>
                {isCreating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Creating...
                  </>
                ) : (
                  'Create University'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default UniversityList;